import { t } from '../../../lib/i18n';
import type { AdminFinanceSummary, FinanceTab } from '../types';
import { type ClientRollupStats, cn, formatChf, formatDayMonth, pluralUk } from '../utils';

interface FinanceStatCardsProps {
  /** The platform-wide figures, straight off `GET /admin/finance/summary`. */
  summary: AdminFinanceSummary;
  /** The populations behind them, folded from the client rollup. */
  stats: ClientRollupStats;
  /** «Вересень 2026» / «Квартал» / «2026». */
  periodLabel: string;
  active: FinanceTab;
  onSelect: (tab: FinanceTab) => void;
}

interface StatCard {
  /**
   * The tab this card filters to when clicked, or `null` when it doesn't
   * filter anything. Deposits used to map to `tab: 'refund'`, a tab that no
   * longer exists — `resolveClientStatus` never emits `FinanceClientStatus:
   * 'refund'`, so that filter always matched zero clients and clicking it
   * always landed on the empty-filter message. The card stays (the figure is
   * real), it just stops pretending to be a filter button.
   */
  tab: FinanceTab | null;
  label: string;
  value: string;
  sub: string;
  /** The 3px inset rule across the card's top — the artboard's only tone cue. */
  rule: string;
  /** Value colour; the row reads as one object unless a number is a problem. */
  tone: string;
}

/** Ukrainian noun after a count, from the shared plural table. */
function countWord(count: number, noun: 'client' | 'invoice' | 'day'): string {
  return pluralUk(count, {
    one: t(`adminFinance.plural.${noun}.one`),
    few: t(`adminFinance.plural.${noun}.few`),
    many: t(`adminFinance.plural.${noun}.many`),
  });
}

/**
 * The four counters over the balances table — `Redesign-D-Admin`, «Фінанси».
 *
 * Each card is a button that filters the table below it, which is the whole
 * argument of the row: the operator reads "CHF 2 170 прострочено", presses it, and
 * is looking at exactly those clients.
 *
 * ── One currency, four separate facts ───────────────────────────────────────
 * Every card is CHF (owner rule 01): invoices are billed in CHF and a deposit
 * IS `users.biddingPower`, CHF at the source. They are still four different
 * facts — a deposit held is the client's money, not a receivable — so nothing
 * on this row is ever added to anything else on it.
 *
 * ── «Дохід» is deliberately NOT the chart's number ──────────────────────────
 * It is `summary.revenueChf` over the selected window, and it can legitimately
 * differ from the revenue card's charted total below: the chart drops an
 * undated invoiced deal that the rollup keeps (dating it now), and it carries a
 * `dealer_subscription` stream the summary figure has no counterpart for. The
 * two therefore carry DIFFERENT labels — «Дохід» here, «Виставлено за період»
 * on the card — so the gap reads as two questions rather than as a bug.
 */
export function FinanceStatCards({
  summary,
  stats,
  periodLabel,
  active,
  onSelect,
}: FinanceStatCardsProps) {
  const overdueSub = summary.overdueChf
    ? t('adminFinance.stats.overdueSub', {
        count: stats.overdueInvoices,
        invoiceWord: countWord(stats.overdueInvoices, 'invoice'),
        client: stats.overdueClient ?? '—',
        days: stats.overdueDays,
        dayWord: countWord(stats.overdueDays, 'day'),
      })
    : t('adminFinance.stats.overdueNone');

  /**
   * `depositsRefundableChf` is NULLABLE and null means UNKNOWN, not zero.
   * Nothing on the server tracks refundability yet — the payments ledger is
   * append-only `deposit|refund|fee` entries with no committed/free split — so
   * the card says so. Rendering `null` as «CHF 0 до повернення» would put an
   * invented number in front of an operator as a fact.
   */
  const refundable = summary.depositsRefundableChf;
  const depositsSub =
    refundable === null || refundable === undefined
      ? t('adminFinance.stats.depositsSubUnknown', {
          count: stats.depositClients,
          clientWord: countWord(stats.depositClients, 'client'),
        })
      : refundable > 0
        ? t('adminFinance.stats.depositsSub', {
            count: stats.depositClients,
            clientWord: countWord(stats.depositClients, 'client'),
            amount: formatChf(refundable),
          })
        : t('adminFinance.stats.depositsSubNone', {
            count: stats.depositClients,
            clientWord: countWord(stats.depositClients, 'client'),
          });

  const cards: StatCard[] = [
    {
      tab: 'due',
      label: t('adminFinance.stats.due'),
      value: formatChf(summary.receivableChf),
      sub: t('adminFinance.stats.dueSub', {
        count: stats.dueClients,
        clientWord: countWord(stats.dueClients, 'client'),
      }),
      rule: 'shadow-[inset_0_3px_0_var(--color-cf-brand)]',
      tone: summary.receivableChf > 0 ? 'text-cf-ink' : 'text-cf-success',
    },
    {
      tab: 'overdue',
      label: t('adminFinance.stats.overdue'),
      value: formatChf(summary.overdueChf),
      sub: overdueSub,
      rule: summary.overdueChf
        ? 'shadow-[inset_0_3px_0_var(--color-cf-danger)]'
        : 'shadow-[inset_0_3px_0_var(--color-cf-success)]',
      tone: summary.overdueChf > 0 ? 'text-cf-danger' : 'text-cf-success',
    },
    {
      tab: null,
      label: t('adminFinance.stats.deposits'),
      value: formatChf(summary.depositsHeldChf),
      sub: depositsSub,
      rule: 'shadow-[inset_0_3px_0_var(--color-cf-info)]',
      tone: 'text-cf-ink',
    },
    {
      tab: 'all',
      label: t('adminFinance.stats.revenue', { period: periodLabel }),
      value: formatChf(summary.revenueChf),
      // No "+14% vs August": nothing on the wire carries a comparison, and the
      // fixture's delta was a sentence the artboard invented. What IS true and
      // worth printing is WHEN the aggregate was taken — `overdueChf` in
      // particular is only correct as of that instant.
      sub: t('adminFinance.stats.computedAt', { at: formatDayMonth(summary.computedAt) }),
      rule: 'shadow-[inset_0_3px_0_var(--color-cf-accent)]',
      tone: 'text-cf-success',
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-2 leading-[normal] lg:grid-cols-[repeat(auto-fit,minmax(220px,1fr))] lg:gap-3">
      {cards.map((card) => {
        const body = (
          <>
            <p className="font-mono text-[10px] tracking-[0.1em] text-cf-ink-subtle uppercase lg:tracking-[0.14em]">
              {card.label}
            </p>
            <p
              className={cn(
                'mt-2 font-mono text-[20px] leading-none font-medium whitespace-nowrap lg:mt-2.5 lg:text-[28px]',
                card.tone,
              )}
            >
              {card.value}
            </p>
            <p className="mt-1 text-[11px] text-cf-ink-subtle lg:mt-1.5 lg:text-[12px]">
              {card.sub}
            </p>
          </>
        );

        // `tab: null` (deposits) filters nothing — see the note on
        // `StatCard.tab` — so it renders as a plain tile, not a button: an
        // element with no click handler and hover cue reads as informational,
        // which is what it actually is.
        if (card.tab === null) {
          return (
            <div
              key={card.label}
              className={cn(
                'rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-3 text-left lg:px-[18px] lg:py-4',
                card.rule,
              )}
            >
              {body}
            </div>
          );
        }

        const tab = card.tab;
        return (
          <button
            key={card.label}
            type="button"
            onClick={() => onSelect(tab)}
            className={cn(
              'rounded-cf-lg border bg-cf-surface px-3.5 py-3 text-left hover:border-cf-brand lg:px-[18px] lg:py-4',
              // «Дохід» maps to the "all" tab, so it would read as permanently
              // selected — the artboard excludes it from the selected border.
              card.tab !== 'all' && active === card.tab ? 'border-cf-brand' : 'border-cf-line',
              card.rule,
            )}
          >
            {body}
          </button>
        );
      })}
    </div>
  );
}
